import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, Download, Trash2, Plus, Upload, Loader2, ShieldCheck, Megaphone, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { API, TOKEN_KEY, fileUrl } from "../lib/api";
import { Crescent } from "../components/Decor";
import { SPONSOR_TIERS } from "../lib/data";

const FORM_TABS = [
  { key: "stay-updated", label: "Stay Updated", cols: [["name", "Name"], ["email", "Email"], ["created_at", "Date"]] },
  { key: "sponsorship", label: "Sponsorships", cols: [["company", "Org"], ["contact_name", "Contact"], ["email", "Email"], ["tier", "Tier"], ["paid", "Paid"], ["created_at", "Date"]] },
  { key: "vendor", label: "Vendors", cols: [["business_name", "Business"], ["email", "Email"], ["category", "Category"], ["total", "Total"], ["paid", "Paid"], ["created_at", "Date"]] },
  { key: "volunteer", label: "Volunteers", cols: [["name", "Name"], ["email", "Email"], ["shift", "Shift"], ["created_at", "Date"]] },
  { key: "talent", label: "Talent", cols: [["name", "Name"], ["email", "Email"], ["talents", "Talents"], ["created_at", "Date"]] },
  { key: "donation", label: "Donations", cols: [["name", "Name"], ["email", "Email"], ["amount", "Amount"], ["paid", "Paid"], ["created_at", "Date"]] },
];

function LoginView({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onLogin(data.user);
    } catch (e2) {
      const d = e2.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center sunburst px-4" data-testid="admin-login">
      <div className="absolute inset-0 sunburst-green pointer-events-none" />
      <div className="stamp p-3 w-full max-w-md relative">
        <div className="stamp-inner p-8 bg-cream">
          <div className="flex flex-col items-center mb-6">
            <Crescent size={56} />
            <h1 className="font-display font-bold text-forest text-3xl mt-3">Organizer Login</h1>
            <p className="eyebrow text-rust mt-1">Pak Fest 2026 · CPTCA</p>
          </div>
          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="field-label">Email</label>
              <input type="email" className="vintage-input" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="admin-email" />
            </div>
            <div>
              <label className="field-label">Password</label>
              <input type="password" className="vintage-input" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="admin-password" />
            </div>
            {err && <p className="font-mono text-xs text-rust" data-testid="admin-error">{err}</p>}
            <button type="submit" disabled={busy} className="btn-rust w-full py-3 text-sm disabled:opacity-60" data-testid="admin-login-btn">
              {busy ? "Signing in…" : "Enter Dashboard"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function SponsorManager() {
  const [sponsors, setSponsors] = useState([]);
  const [form, setForm] = useState({ name: "", tier: "Community", website: "" });
  const [logoPath, setLogoPath] = useState(null);
  const [logoName, setLogoName] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/sponsors").then((r) => setSponsors(r.data));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogoPath(r.data.path); setLogoName(file.name);
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const add = async (e) => {
    e.preventDefault();
    if (!form.name) return toast.error("Sponsor name required");
    try {
      await api.post("/admin/sponsors", { ...form, logo_path: logoPath });
      toast.success("Sponsor added");
      setForm({ name: "", tier: "Community", website: "" }); setLogoPath(null); setLogoName("");
      load();
    } catch { toast.error("Failed to add sponsor"); }
  };

  const remove = async (id) => {
    await api.delete(`/admin/sponsors/${id}`);
    toast.success("Removed"); load();
  };

  return (
    <div data-testid="sponsor-manager">
      <form onSubmit={add} className="border-2 border-forest p-5 mb-8 grid md:grid-cols-4 gap-4 items-end bg-cream">
        <div>
          <label className="field-label">Name</label>
          <input className="vintage-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sponsor-name" />
        </div>
        <div>
          <label className="field-label">Tier</label>
          <select className="vintage-input" value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} data-testid="sponsor-tier">
            {SPONSOR_TIERS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="field-label">Website</label>
          <input className="vintage-input" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://" data-testid="sponsor-website" />
        </div>
        <div>
          <label className="field-label">Logo</label>
          <label className="flex items-center gap-2 border-2 border-dashed border-forest px-3 py-2 cursor-pointer text-xs font-mono">
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
            <span className="truncate">{logoName || "Upload"}</span>
            <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="sponsor-logo" />
          </label>
        </div>
        <button type="submit" className="btn-rust px-4 py-2 text-xs md:col-span-4 justify-self-start inline-flex items-center gap-2" data-testid="sponsor-add-btn">
          <Plus size={16} /> Add Sponsor
        </button>
      </form>

      <div className="flex flex-wrap gap-4">
        {sponsors.length === 0 && <p className="font-mono text-sm text-forest-light">No sponsors yet.</p>}
        {sponsors.map((s) => (
          <div key={s.id} className="border-2 border-forest/40 p-3 w-40 text-center relative" data-testid={`manage-sponsor-${s.id}`}>
            <button onClick={() => remove(s.id)} className="absolute -top-2 -right-2 bg-rust text-cream w-6 h-6 flex items-center justify-center border border-forest" data-testid={`delete-sponsor-${s.id}`}>
              <Trash2 size={13} />
            </button>
            <div className="h-20 flex items-center justify-center mb-2">
              {s.logo_path ? <img src={fileUrl(s.logo_path)} alt={s.name} className="max-h-20 object-contain" /> : <span className="numeral text-2xl text-forest">{s.name}</span>}
            </div>
            <div className="font-display font-semibold text-forest text-sm leading-tight">{s.name}</div>
            <div className="eyebrow text-rust text-[0.55rem] mt-1">{s.tier}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Dashboard({ user, onLogout }) {
  const [tab, setTab] = useState("stay-updated");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const isSponsorMgr = tab === "sponsors-mgr";

  useEffect(() => {
    if (isSponsorMgr) return;
    setLoading(true);
    api.get(`/admin/submissions/${tab}`).then((r) => setRows(r.data)).catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  }, [tab, isSponsorMgr]);

  const current = FORM_TABS.find((t) => t.key === tab);
  const exportCsv = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    window.open(`${API}/admin/export/${tab}?token=${token}`, "_blank");
  };

  const fmt = (col, v) => {
    if (col === "paid") return v ? "✓ Paid" : "—";
    if (v == null) return "—";
    if (col === "created_at") return new Date(v).toLocaleString();
    if (col === "total" || col === "amount") return `$${v}`;
    return String(v);
  };

  const [publishingId, setPublishingId] = useState(null);
  const publishSponsor = async (row) => {
    setPublishingId(row.id);
    try {
      await api.post(`/admin/sponsorships/${row.id}/publish`);
      toast.success(`${row.company} is now live on the Sponsors page`);
      setRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, sponsor_id: "published" } : r)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Publish failed");
    } finally {
      setPublishingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-cream-muted" data-testid="admin-dashboard">
      <header className="bg-forest text-cream border-b-2 border-gold">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldCheck size={24} className="text-gold" />
            <span className="numeral text-2xl tracking-wide">PAK FEST 2026 · ADMIN</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="font-mono text-xs text-cream/70 hidden sm:block">{user.email}</span>
            <button onClick={onLogout} className="btn-ghost px-4 py-2 text-xs inline-flex items-center gap-2" data-testid="admin-logout"><LogOut size={14} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        <div className="flex flex-wrap gap-2 mb-8">
          {FORM_TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} className={`px-4 py-2 border-2 font-mono uppercase tracking-wide text-xs transition-colors ${tab === t.key ? "bg-forest text-cream border-forest" : "bg-cream text-forest border-forest/40"}`} data-testid={`admin-tab-${t.key}`}>
              {t.label}
            </button>
          ))}
          <button onClick={() => setTab("sponsors-mgr")} className={`px-4 py-2 border-2 font-mono uppercase tracking-wide text-xs transition-colors ${isSponsorMgr ? "bg-rust text-cream border-rust" : "bg-cream text-rust border-rust/50"}`} data-testid="admin-tab-sponsors">
            Manage Sponsors
          </button>
        </div>

        {isSponsorMgr ? (
          <SponsorManager />
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-forest text-2xl">{current.label} <span className="numeral text-rust ml-2">{rows.length}</span></h2>
              <button onClick={exportCsv} className="btn-forest px-4 py-2 text-xs inline-flex items-center gap-2" data-testid="admin-export-btn"><Download size={14} /> Export CSV</button>
            </div>
            <div className="border-2 border-forest bg-cream overflow-x-auto">
              <table className="w-full text-left" data-testid="admin-table">
                <thead className="bg-forest text-cream">
                  <tr>{current.cols.map(([, l]) => <th key={l} className="eyebrow text-[0.6rem] px-4 py-3">{l}</th>)}{tab === "sponsorship" && <th className="eyebrow text-[0.6rem] px-4 py-3">Publish</th>}</tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={current.cols.length + (tab === "sponsorship" ? 1 : 0)} className="px-4 py-8 text-center font-mono text-sm text-forest-light">Loading…</td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan={current.cols.length + (tab === "sponsorship" ? 1 : 0)} className="px-4 py-8 text-center font-mono text-sm text-forest-light">No submissions yet.</td></tr>
                  ) : rows.map((r, i) => (
                    <tr key={i} className="border-t border-forest/20 hover:bg-cream-muted">
                      {current.cols.map(([c]) => <td key={c} className="font-mono text-xs md:text-sm px-4 py-3 text-forest-light">{fmt(c, r[c])}</td>)}
                      {tab === "sponsorship" && (
                        <td className="px-4 py-3" data-testid={`sponsorship-publish-cell-${r.id}`}>
                          {r.sponsor_id ? (
                            <span className="inline-flex items-center gap-1 font-mono text-xs text-forest" data-testid={`sponsorship-published-${r.id}`}><CheckCircle2 size={14} /> Published</span>
                          ) : (
                            <button onClick={() => publishSponsor(r)} disabled={publishingId === r.id} className="btn-rust px-3 py-1.5 text-[0.65rem] inline-flex items-center gap-1.5 disabled:opacity-60" data-testid={`sponsorship-publish-btn-${r.id}`}>
                              {publishingId === r.id ? <Loader2 size={13} className="animate-spin" /> : <Megaphone size={13} />} Publish
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setChecking(false); return; }
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => localStorage.removeItem(TOKEN_KEY)).finally(() => setChecking(false));
  }, []);

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setUser(null); navigate("/admin"); };

  if (checking) return <div className="min-h-screen flex items-center justify-center font-mono text-forest">Loading…</div>;
  if (!user) return <LoginView onLogin={setUser} />;
  return <Dashboard user={user} onLogout={logout} />;
}
